import { useCallback, useState } from "react";
import { analyzeImage } from "../lib/analyzeImage";
import { fileToImage, imageToRgba } from "../lib/loadImage";
import type { DetectionResult } from "../vision";

export type DetectionStatus = "idle" | "analyzing" | "done" | "error";

export interface DominoDetectionState {
  status: DetectionStatus;
  result: DetectionResult | null;
  error: string | null;
  /** Object URL of the most recently analysed image, for preview. */
  previewUrl: string | null;
  analyzeFile: (file: File) => Promise<void>;
  reset: () => void;
}

/** Dependencies, injectable so the hook can be unit tested without a canvas. */
export interface DetectionDeps {
  loadImage: typeof fileToImage;
  extractRgba: typeof imageToRgba;
  analyze: typeof analyzeImage;
  createPreviewUrl: (file: File) => string;
  revokePreviewUrl: (url: string) => void;
}

const defaultDeps: DetectionDeps = {
  loadImage: fileToImage,
  extractRgba: imageToRgba,
  analyze: analyzeImage,
  createPreviewUrl: (file) => URL.createObjectURL(file),
  revokePreviewUrl: (url) => URL.revokeObjectURL(url),
};

export function useDominoDetection(
  deps: Partial<DetectionDeps> = {},
): DominoDetectionState {
  const d = { ...defaultDeps, ...deps };
  const [status, setStatus] = useState<DetectionStatus>("idle");
  const [result, setResult] = useState<DetectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const analyzeFile = useCallback(
    async (file: File) => {
      setStatus("analyzing");
      setError(null);
      setResult(null);
      setPreviewUrl((prev) => {
        if (prev) d.revokePreviewUrl(prev);
        return d.createPreviewUrl(file);
      });

      try {
        const image = await d.loadImage(file);
        const rgba = d.extractRgba(image);
        const detection = d.analyze(rgba);
        setResult(detection);
        setStatus("done");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to analyze the image.");
        setStatus("error");
      }
    },
    [d],
  );

  const reset = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) d.revokePreviewUrl(prev);
      return null;
    });
    setStatus("idle");
    setResult(null);
    setError(null);
  }, [d]);

  return { status, result, error, previewUrl, analyzeFile, reset };
}
